import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AlignLeft, AlignCenter, AlignRight, Check, X, AlignJustify, MessageSquare } from "lucide-react";
import { STORY_FONTS, DEFAULT_STORY_FONT, ensureAllStoryFonts, useStoryFontReady } from "@/lib/storyFonts";
import { textStickerStyle, BUBBLE_MAX_CHARS, LONG_TEXT_MAX_CHARS, LONG_TEXT_RATIO } from "@/components/StoryMedia";
import { VSlider } from "./StoryEditorBars";
import { DRAW_PALETTE, PILL_MODES, applyPill, baseColorOf, pillModeOf, clamp } from "./storyEditorUtils";

const MIN_PX = 14, MAX_PX = 72;
const LONG_PREVIEW_PX = 22; // taille d'édition d'un texte long (la taille finale est ajustée au cadre par fitBubble)
const ALIGNS = ["left", "center", "right"];
const AlignIcon = { left: AlignLeft, center: AlignCenter, right: AlignRight };

// Outil texte plein écran : on tape, on choisit police / alignement / pastille / couleur / taille,
// « Terminé » pose (ou met à jour) une bulle déplaçable sur la story.
// Lot 713 — bulle limitée à 300 caractères (compteur) ; à la limite, « Texte long » bascule en texte principal
// centré et ajusté au cadre (600 caractères) ; « Bulle » revient en arrière tant que le texte tient en 300.
export const StoryTextTool = ({ initial, frameWidth, onDone, onCancel, t }) => {
  const [text, setText] = useState(initial?.text || "");
  const [font, setFont] = useState(initial?.font || DEFAULT_STORY_FONT);
  const [align, setAlign] = useState(initial?.align || "center");
  const [pill, setPill] = useState(pillModeOf(initial));
  const [color, setColor] = useState(baseColorOf(initial));
  const [long, setLong] = useState(!!initial?.long);
  const [px, setPx] = useState(initial && !initial.long ? clamp(Math.round(initial.scale * frameWidth), MIN_PX, MAX_PX) : 30);
  const taRef = useRef(null);
  const mirrorRef = useRef(null);
  const [box, setBox] = useState({ w: 40, h: 40 });
  useEffect(() => { ensureAllStoryFonts(); taRef.current?.focus(); }, []);

  const limit = long ? LONG_TEXT_MAX_CHARS : BUBBLE_MAX_CHARS;
  const shownPx = long ? LONG_PREVIEW_PX : px;
  const fontReady = useStoryFontReady(font);
  const styled = { text, font, align, ...applyPill(pill, color), scale: shownPx / frameWidth };
  const style = textStickerStyle(styled, `${shownPx}px`);

  // Le miroir invisible mesure le texte → la zone de saisie (et sa pastille) épouse le contenu
  // (re-mesuré quand la police Google finit de charger).
  useLayoutEffect(() => {
    const m = mirrorRef.current;
    if (m) setBox({ w: Math.ceil(m.offsetWidth) + 2, h: Math.ceil(m.offsetHeight) + 2 });
  }, [text, font, align, pill, shownPx, fontReady]);

  const done = () => {
    const v = text.trim();
    if (!v) { onCancel(); return; }
    onDone({ text: v, font, align, color: styled.color, bg: styled.bg, scale: long ? LONG_TEXT_RATIO : px / frameWidth, long: long || undefined });
  };
  const cycle = (arr, cur) => arr[(arr.indexOf(cur) + 1) % arr.length];
  const AIcon = AlignIcon[align];
  const pillStyle = pill === "none" ? { color: "#fff", border: "1.5px solid rgba(255,255,255,.8)" } : pill === "solid" ? { background: "#fff", color: "#111" } : { background: "rgba(255,255,255,.35)", color: "#fff" };
  const atLimit = text.length >= limit;
  const showCounter = long || text.length >= limit - 60;

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-black/70 backdrop-blur-sm" data-testid="story-text-tool" data-long={long ? "1" : "0"}>
      <div className="px-3 flex items-center justify-between gap-2" style={{ paddingTop: "calc(var(--sa-top, 0px) + 10px)" }}>
        <button type="button" onClick={onCancel} aria-label={t("common.cancel", "Annuler")} className="h-10 w-10 grid place-items-center rounded-full bg-black/40 text-white" data-testid="story-text-cancel"><X size={20} /></button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setAlign(cycle(ALIGNS, align))} aria-label={t("story.text_align", "Alignement")} className="h-10 w-10 grid place-items-center rounded-full bg-black/40 text-white" data-testid="story-text-align" data-align={align}><AIcon size={19} /></button>
          <button type="button" onClick={() => setPill(cycle(PILL_MODES, pill))} aria-label={t("story.text_pill", "Fond du texte")} className="h-10 w-10 grid place-items-center rounded-full bg-black/40" data-testid="story-text-pill" data-pill={pill}>
            <span className="h-6 w-6 rounded-md grid place-items-center text-[13px] font-bold" style={pillStyle}>A</span>
          </button>
        </div>
        <button type="button" onClick={done} className="h-10 px-4 rounded-full text-white text-sm font-bold flex items-center gap-1 active:scale-95 transition-transform" style={{ background: "var(--yalola-primary)" }} data-testid="story-text-done">
          <Check size={16} /> {t("story.done", "Terminé")}
        </button>
      </div>

      <div className="flex gap-2 px-3 mt-3 overflow-x-auto hide-scrollbar" role="radiogroup" data-testid="story-text-fonts">
        {STORY_FONTS.map((f) => {
          const on = font === f.key;
          return (
            <button key={f.key} type="button" role="radio" aria-checked={on} onClick={() => setFont(f.key)} className="shrink-0 h-9 px-3 rounded-full text-sm border transition-colors" style={{ ...f.style, background: on ? "#fff" : "rgba(0,0,0,.4)", color: on ? "#111" : "#fff", borderColor: on ? "#fff" : "rgba(255,255,255,.25)" }} data-testid={`story-text-font-${f.key}`}>
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Lot 713 — compteur + bascule bulle ↔ texte long */}
      {(showCounter || long) && (
        <div className="flex items-center justify-center gap-2 px-3 mt-2 flex-wrap" data-testid="story-text-limit-row">
          <span className={`text-[11px] font-semibold tabular-nums ${atLimit ? "text-amber-300" : "text-white/70"}`} data-testid="story-text-counter">{text.length}/{limit}</span>
          {!long && atLimit && (
            <button type="button" onClick={() => setLong(true)} className="h-8 px-3 rounded-full text-[12px] font-bold text-white flex items-center gap-1 active:scale-95 transition-transform" style={{ background: "var(--yalola-primary)" }} data-testid="story-text-long-btn">
              <AlignJustify size={14} /> {t("story.long_text", "Texte long")}
            </button>
          )}
          {long && (
            <>
              <span className="text-[11px] text-white/70" data-testid="story-text-long-hint">{t("story.long_text_hint", "Centré, taille ajustée au cadre")}</span>
              {text.length <= BUBBLE_MAX_CHARS && (
                <button type="button" onClick={() => setLong(false)} className="h-8 px-3 rounded-full text-[12px] font-bold text-white flex items-center gap-1 bg-black/40 border border-white/30 active:scale-95 transition-transform" data-testid="story-text-bubble-btn">
                  <MessageSquare size={14} /> {t("story.bubble_mode", "Bulle")}
                </button>
              )}
            </>
          )}
        </div>
      )}

      <div className="relative flex-1 min-h-0 flex flex-col items-center px-12 pt-[7vh]">
        {!long && <VSlider value={(px - MIN_PX) / (MAX_PX - MIN_PX)} onChange={(v) => setPx(Math.round(MIN_PX + v * (MAX_PX - MIN_PX)))} className="absolute left-1.5 top-[6vh] z-10" testId="story-text-size-vslider" />}
        {/* Miroir de mesure (même style, largeur max 85 % du cadre) → la zone de saisie et sa pastille épousent le texte */}
        <span ref={mirrorRef} aria-hidden className="absolute left-12 top-0 invisible pointer-events-none" style={{ ...style, maxWidth: `${Math.round(frameWidth * 0.85)}px`, minWidth: "1ch" }}>{text || t("story.tap_to_type", "Touche pour écrire")}{text.endsWith("\n") ? " " : ""}</span>
        <div className="relative" style={{ width: box.w, height: box.h, maxWidth: "none" }}>
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, limit))}
            placeholder={t("story.tap_to_type", "Touche pour écrire")}
            rows={1}
            maxLength={limit}
            className="absolute inset-0 resize-none outline-none border-0 bg-transparent placeholder:text-white/40 overflow-hidden"
            style={{ ...style, width: "100%", height: "100%", caretColor: "#fff" }}
            data-testid="story-text-textarea"
          />
        </div>
        <div className="flex items-center gap-2.5 mt-6 overflow-x-auto hide-scrollbar max-w-full pb-2" data-testid="story-text-colors">
          {DRAW_PALETTE.map((c) => (
            <button key={c} type="button" onClick={() => setColor(c)} aria-label={c} className="h-8 w-8 shrink-0 rounded-full border-2 transition-transform active:scale-90" style={{ background: c, borderColor: color === c ? "var(--yalola-primary)" : "rgba(255,255,255,.7)", transform: color === c ? "scale(1.15)" : "none" }} data-testid={`story-text-color-${c.replace("#", "")}`} />
          ))}
        </div>
      </div>
    </div>
  );
};
